/**
 * SimulationResults Component
 * Displays formatted results from game simulations
 */

import { useState } from 'react';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import * as d3 from 'd3';
import { generateMoveNarrative } from './simulation';
import { BoardHeatmap } from './BoardHeatmap';

/**
 * Accordion component matching pool simulation style
 */
const Accordion = ({ title, defaultOpen = false, children }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);

    return (
        <div className="w-full">
            <div
                className={`flex flex-row hover:cursor-pointer items-center justify-between py-3 px-2 ${isOpen ? "border-b border-gray-200" : ""}`}
                onClick={() => setIsOpen(!isOpen)}
            >
                <p className="text-sm sm:text-base font-medium text-gray-800">{title}</p>
                {isOpen ? (
                    <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                ) : (
                    <ChevronRightIcon className="h-5 w-5 text-gray-500" />
                )}
            </div>
            {isOpen && (
                <div className="p-2 sm:p-4">
                    {children}
                </div>
            )}
        </div>
    );
};

/**
 * Stat card component
 */
const StatCard = ({ label, value, sublabel, highlight = false }) => (
    <div className={`rounded-lg p-3 ${highlight ? 'bg-badger-purple/10 border border-badger-purple/30' : 'bg-gray-50 border border-gray-200'}`}>
        <div className="text-xs text-gray-500 uppercase tracking-wide">{label}</div>
        <div className={`text-xl font-bold ${highlight ? 'text-badger-purple' : 'text-gray-900'}`}>{value}</div>
        {sublabel && <div className="text-xs text-gray-400">{sublabel}</div>}
    </div>
);

/**
 * Lollipop Chart for turn distribution
 */
const TurnDistributionChart = ({ gameResults, medianRolls }) => {
    const rollCounts = gameResults.map(g => g.totalRolls);

    // Group by roll count
    const distribution = d3.rollup(rollCounts, v => v.length, d => d);
    const data = Array.from(distribution, ([rolls, count]) => ({ rolls, count }))
        .sort((a, b) => a.rolls - b.rolls);

    if (data.length === 0) return null;

    const minRolls = d3.min(data, d => d.rolls);
    const maxRolls = d3.max(data, d => d.rolls);
    const maxCount = d3.max(data, d => d.count);

    // SVG dimensions
    const width = 400;
    const height = 180;
    const margin = { top: 20, right: 10, bottom: 35, left: 10 };
    const boundedWidth = width - margin.left - margin.right;
    const boundedHeight = height - margin.top - margin.bottom;

    // Scales
    const xScale = d3.scaleLinear()
        .domain([minRolls - 1, maxRolls + 1])
        .range([0, boundedWidth]);

    const yScale = d3.scaleLinear()
        .domain([0, maxCount * 1.1])
        .range([boundedHeight, 0]);

    // X-axis ticks
    const xTicks = d3.range(
        Math.ceil(minRolls / 5) * 5,
        maxRolls + 1,
        Math.max(5, Math.floor((maxRolls - minRolls) / 8))
    );

    return (
        <div className="w-full overflow-x-auto">
            <svg viewBox={`0 0 ${width} ${height}`} className="w-full max-w-md">
                <g transform={`translate(${margin.left}, ${margin.top})`}>
                    {/* Median line */}
                    <line
                        x1={xScale(medianRolls)}
                        x2={xScale(medianRolls)}
                        y1={0}
                        y2={boundedHeight}
                        stroke="#3B82F6"
                        strokeWidth={2}
                        strokeDasharray="4,4"
                    />
                    <text
                        x={xScale(medianRolls)}
                        y={-6}
                        textAnchor="middle"
                        fontSize={10}
                        fill="#3B82F6"
                    >
                        median: {medianRolls}
                    </text>

                    {/* Vertical bars */}
                    {data.map(d => (
                        <line
                            key={d.rolls}
                            x1={xScale(d.rolls)}
                            x2={xScale(d.rolls)}
                            y1={yScale(0)}
                            y2={yScale(d.count)}
                            stroke="#A26ECC"
                            strokeWidth={2}
                            strokeLinecap="round"
                        />
                    ))}

                    {/* X-axis */}
                    <line
                        x1={0}
                        x2={boundedWidth}
                        y1={boundedHeight}
                        y2={boundedHeight}
                        stroke="#E5E7EB"
                        strokeWidth={1}
                    />
                    {xTicks.map(tick => (
                        <g key={tick} transform={`translate(${xScale(tick)}, ${boundedHeight})`}>
                            <line y2={5} stroke="#9CA3AF" />
                            <text y={18} textAnchor="middle" fontSize={10} fill="#6B7280">
                                {tick}
                            </text>
                        </g>
                    ))}
                    <text
                        x={boundedWidth / 2}
                        y={boundedHeight + 32}
                        textAnchor="middle"
                        fontSize={11}
                        fill="#6B7280"
                    >
                        Number of Rolls to Win
                    </text>

                    {/* Y-axis */}
                    <line
                        x1={0}
                        x2={0}
                        y1={0}
                        y2={boundedHeight}
                        stroke="#E5E7EB"
                        strokeWidth={1}
                    />
                </g>
            </svg>
        </div>
    );
};

/**
 * Horizontal bar for frequency display
 */
const FrequencyBar = ({ label, value, maxValue, color = 'bg-badger-purple' }) => (
    <div className="flex items-center gap-2 text-sm">
        <span className="w-20 text-right font-medium text-gray-600">{label}</span>
        <div className="flex-1 bg-gray-100 rounded h-4 overflow-hidden">
            <div
                className={`h-full ${color}`}
                style={{ width: `${(value / maxValue) * 100}%` }}
            />
        </div>
        <span className="w-12 text-gray-500">{value.toLocaleString()}</span>
    </div>
);

/**
 * Single Game Results Display
 */
export const SingleGameResults = ({ result }) => {
    if (!result) return null;

    const { totalRolls, snakesHit, laddersHit, moves, diceDistribution, landingCounts, config, snakeHits, ladderHits } = result;
    const { rows, cols, snakes, ladders, startPosition, dice } = config;
    const diceLabel = dice ? `${dice.count}d${dice.sides}` : '1d6';

    // Get top landing squares
    const topLandings = Object.entries(landingCounts)
        .filter(([_, count]) => count > 0)
        .sort(([_, a], [__, b]) => b - a)
        .slice(0, 10);

    return (
        <div className="space-y-6">
            {/* Two-column layout */}
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-start">
                {/* Left: Heatmap */}
                <div className="lg:col-span-3">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Landing Heatmap</h4>
                    <BoardHeatmap
                        rows={rows}
                        cols={cols}
                        maxPixels={450}
                        landingCounts={landingCounts}
                        snakes={snakes}
                        ladders={ladders}
                        snakeHits={snakeHits}
                        ladderHits={ladderHits}
                        simpleArrows={true}
                    />
                </div>

                {/* Right: Stats */}
                <div className="lg:col-span-2 space-y-4">
                    <h4 className="text-sm font-medium text-gray-700">Game Summary</h4>
                    <div className="grid grid-cols-2 gap-3">
                        <StatCard label="Start" value={startPosition || 1} />
                        <StatCard label="Dice" value={diceLabel} />
                        <StatCard label="Rolls" value={totalRolls} highlight />
                        <StatCard label="Snakes" value={snakesHit} />
                        <StatCard label="Ladders" value={laddersHit} />
                    </div>

                    {/* Dice Distribution */}
                    <div className="pt-2">
                        <h5 className="text-xs font-medium text-gray-500 uppercase mb-2">Dice Rolls</h5>
                        <div className="flex flex-wrap gap-2">
                            {Object.entries(diceDistribution)
                                .sort(([a], [b]) => parseInt(a) - parseInt(b))
                                .map(([roll, count]) => (
                                    <div key={roll} className="bg-gray-100 px-2 py-1 rounded text-xs">
                                        <span className="font-medium">{roll}:</span> {count}
                                    </div>
                                ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Accordions */}
            <div className="border border-gray-200 rounded-lg divide-y divide-gray-200">
                <Accordion title="Most Visited Squares" defaultOpen={false}>
                    <div className="space-y-1">
                        {topLandings.map(([square, count]) => (
                            <FrequencyBar
                                key={square}
                                label={`#${square}`}
                                value={count}
                                maxValue={topLandings[0][1]}
                            />
                        ))}
                    </div>
                </Accordion>

                <Accordion title={`Move History (${moves.length} moves)`} defaultOpen={false}>
                    <div className="max-h-64 overflow-y-auto space-y-1 text-sm">
                        {moves.map((move, index) => (
                            <div key={index} className="py-1 border-b border-gray-100 last:border-0">
                                <span className="text-gray-400 mr-2">{index + 1}.</span>
                                <span className={
                                    move.hitSnake ? 'text-badger-purple' :
                                    move.hitLadder ? 'text-yellow-600' :
                                    move.won ? 'text-green-600 font-bold' :
                                    'text-gray-700'
                                }>
                                    {generateMoveNarrative(move)}
                                </span>
                            </div>
                        ))}
                    </div>
                </Accordion>
            </div>
        </div>
    );
};

/**
 * Multi-Game Results Display
 */
export const MultiGameResults = ({ result }) => {
    if (!result) return null;

    const {
        gamesSimulated,
        averageRolls,
        minRolls,
        maxRolls,
        averageSnakesHit,
        averageLaddersHit,
        aggregatedLandingCounts,
        aggregatedSnakeHits,
        aggregatedLadderHits,
        rollDistribution,
        gameResults,
        config
    } = result;

    const { rows, cols, snakes, ladders, startPosition, dice } = config;
    const diceLabel = dice ? `${dice.count}d${dice.sides}` : '1d6';

    // Calculate median rolls
    const sortedRolls = gameResults.map(g => g.totalRolls).sort((a, b) => a - b);
    const medianRolls = sortedRolls[Math.floor(sortedRolls.length / 2)];

    // Get top landing squares
    const topLandings = Object.entries(aggregatedLandingCounts)
        .filter(([_, count]) => count > 0)
        .sort(([_, a], [__, b]) => b - a)
        .slice(0, 10);

    // Sort snake/ladder hits
    const sortedSnakeHits = Object.entries(aggregatedSnakeHits)
        .filter(([_, count]) => count > 0)
        .sort(([_, a], [__, b]) => b - a);
    const sortedLadderHits = Object.entries(aggregatedLadderHits)
        .filter(([_, count]) => count > 0)
        .sort(([_, a], [__, b]) => b - a);

    return (
        <div className="space-y-6">
            {/* Two-column layout */}
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-start">
                {/* Left: Heatmap */}
                <div className="lg:col-span-3">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Landing Frequency Heatmap</h4>
                    <BoardHeatmap
                        rows={rows}
                        cols={cols}
                        maxPixels={500}
                        landingCounts={aggregatedLandingCounts}
                        snakes={snakes}
                        ladders={ladders}
                        snakeHits={aggregatedSnakeHits}
                        ladderHits={aggregatedLadderHits}
                        simpleArrows={true}
                    />
                </div>

                {/* Right: Stats and Distribution */}
                <div className="lg:col-span-2 space-y-5">
                    {/* Config summary */}
                    <div className="bg-gray-50 rounded-lg p-3 text-sm">
                        <span className="font-medium">{gamesSimulated.toLocaleString()}</span> games simulated
                        <span className="text-gray-400 mx-1">•</span>
                        Start: <span className="font-medium">{startPosition || 1}</span>
                        <span className="text-gray-400 mx-1">•</span>
                        Dice: <span className="font-medium">{diceLabel}</span>
                    </div>

                    {/* Key stats */}
                    <div className="grid grid-cols-3 gap-3">
                        <StatCard label="Avg Rolls" value={averageRolls} highlight />
                        <StatCard label="Min" value={minRolls} />
                        <StatCard label="Max" value={maxRolls} />
                    </div>

                    {/* Turn Distribution Chart */}
                    <div>
                        <h5 className="text-xs font-medium text-gray-500 uppercase mb-2">Turns Distribution</h5>
                        <TurnDistributionChart
                            gameResults={gameResults}
                            medianRolls={medianRolls}
                        />
                    </div>

                    {/* Snake/Ladder averages */}
                    <div className="grid grid-cols-2 gap-3">
                        <StatCard label="Avg Snakes" value={averageSnakesHit} sublabel="per game" />
                        <StatCard label="Avg Ladders" value={averageLaddersHit} sublabel="per game" />
                    </div>
                </div>
            </div>

            {/* Accordions */}
            <div className="border border-gray-200 rounded-lg divide-y divide-gray-200">
                <Accordion title="Most Visited Squares" defaultOpen={false}>
                    <div className="space-y-1">
                        {topLandings.map(([square, count]) => (
                            <FrequencyBar
                                key={square}
                                label={`#${square}`}
                                value={count}
                                maxValue={topLandings[0][1]}
                            />
                        ))}
                    </div>
                </Accordion>

                <Accordion title="Snake Hit Frequency" defaultOpen={false}>
                    <div className="space-y-1">
                        {sortedSnakeHits.map(([key, count]) => {
                            const [start, end] = key.split('-');
                            return (
                                <FrequencyBar
                                    key={key}
                                    label={`${start}→${end}`}
                                    value={count}
                                    maxValue={sortedSnakeHits[0]?.[1] || 1}
                                    color="bg-badger-purple/70"
                                />
                            );
                        })}
                    </div>
                </Accordion>

                <Accordion title="Ladder Hit Frequency" defaultOpen={false}>
                    <div className="space-y-1">
                        {sortedLadderHits.map(([key, count]) => {
                            const [start, end] = key.split('-');
                            return (
                                <FrequencyBar
                                    key={key}
                                    label={`${start}→${end}`}
                                    value={count}
                                    maxValue={sortedLadderHits[0]?.[1] || 1}
                                    color="bg-yellow-400"
                                />
                            );
                        })}
                    </div>
                </Accordion>

                <Accordion title="Dice Roll Distribution" defaultOpen={false}>
                    <div className="flex flex-wrap gap-2">
                        {Object.entries(rollDistribution)
                            .sort(([a], [b]) => parseInt(a) - parseInt(b))
                            .map(([roll, count]) => (
                                <div key={roll} className="bg-gray-100 px-3 py-1 rounded text-sm">
                                    <span className="font-medium">{roll}:</span> {count.toLocaleString()}
                                </div>
                            ))}
                    </div>
                </Accordion>
            </div>
        </div>
    );
};

export default SingleGameResults;
